// Thin fetch wrapper. Backend FastAPI app lives at src/flowstack/server/
// (B0 PR-252 landed auth routes; B1 added projects/runs/artifacts/budget
// readers).
//
// M9 #307 — split deployment, mirroring orbit/portal-ui/src/api.ts:
//   * SPA at https://flowstack.attmosdiscovery.com/ (S3 + CloudFront).
//   * API at https://flowstack-api.attmosdiscovery.com/ (AWS App Runner).
// portal-config.json is fetched at runtime to learn the API endpoint —
// no VITE_API_BASE_URL build-time injection so the same artifact can
// ship to dev + prod without a rebuild.
//
// For local development the Vite proxy in vite.config.ts redirects
// /api/* to localhost:8080 and portal-config.json's apiEndpoint is
// ignored when the resolved URL stays on the current origin.
//
// ETag-aware polling per P1-WEBAPP §2.4: every GET caches the last
// response's ETag keyed by path and replays it as If-None-Match on the
// next call to the same path. Server replies with 304 (no body) when
// nothing changed; the wrapper transparently returns the cached payload.
// Cache lives for the page lifetime — TanStack Query's own staleTime
// handles longer windows.

export interface PortalConfig {
  /** Base URL the API is served from. Example:
   *  `https://flowstack-api.attmosdiscovery.com`.
   *
   *  When this matches the current origin (or when empty), apiFetch
   *  treats paths as origin-relative — that's the local-dev path with
   *  the Vite proxy.
   */
  apiEndpoint: string;
}

// In-memory cache for portal-config.json. The S3 deploy uploads
// portal-config.json with ``no-cache`` so a flipped flag lands on the
// CDN edge immediately. The 60s TTL here lets the SPA pick that up
// within a minute without re-fetching the config on every API call.
// Mirrors orbit/portal-ui/src/api.ts:CONFIG_TTL_MS.
const PORTAL_CONFIG_TTL_MS = 60_000;
let _portalConfigCache: PortalConfig | null = null;
let _portalConfigExpiresAt = 0;

// Fallback config used when portal-config.json is missing or
// unreachable. Empty apiEndpoint means "origin-relative", which is
// the same behavior we had before #307 landed. Local unit tests that
// don't mock portal-config.json land here, as does the local dev
// flow when running off the Vite proxy without a portal-config.json
// in public/.
const _PORTAL_CONFIG_FALLBACK: PortalConfig = { apiEndpoint: "" };

// Shorter TTL when we fell back due to a server error so recovery
// happens within ~5s rather than waiting out the full 60s. Per
// Rupert PR-308 r1 #6: a 5xx silently caching for 60s suppresses
// the signal needed to diagnose a misconfig.
const PORTAL_CONFIG_5XX_TTL_MS = 5_000;

async function _loadPortalConfig(): Promise<PortalConfig> {
  if (_portalConfigCache && Date.now() < _portalConfigExpiresAt) {
    return _portalConfigCache;
  }
  try {
    const res = await fetch(new URL("portal-config.json", window.location.origin).href);
    if (!res.ok) {
      // Per Rupert PR-308 r1 #6: distinguish "missing" (404, normal
      // local-dev path) from "broken" (401/403/5xx, real misconfig).
      // Surface the broken case to the console so an operator can
      // diagnose; cache the broken case for a much shorter TTL so
      // recovery is fast.
      if (res.status !== 404) {
        console.warn(
          `portal-config.json fetch returned ${res.status}; falling back ` +
            `to origin-relative apiEndpoint (cached for 5s).`
        );
      }
      const next = _portalConfigCache ?? _PORTAL_CONFIG_FALLBACK;
      _portalConfigCache = next;
      _portalConfigExpiresAt =
        Date.now() + (res.status === 404 ? PORTAL_CONFIG_TTL_MS : PORTAL_CONFIG_5XX_TTL_MS);
      return next;
    }
    _portalConfigCache = (await res.json()) as PortalConfig;
    _portalConfigExpiresAt = Date.now() + PORTAL_CONFIG_TTL_MS;
    return _portalConfigCache;
  } catch {
    // Network failure (CSP, offline, fetch rejected). Cache the
    // origin-relative fallback briefly so a temporary outage
    // doesn't trigger a fetch storm on every apiFetch call but
    // recovery is still quick.
    const next = _portalConfigCache ?? _PORTAL_CONFIG_FALLBACK;
    _portalConfigCache = next;
    _portalConfigExpiresAt = Date.now() + PORTAL_CONFIG_5XX_TTL_MS;
    return next;
  }
}

// Per Rupert PR-308 r1 #1 + r2 #6: portal-config.json comes from S3
// (anyone who controls the bucket can rewrite it). Before sending
// session cookies (credentials: 'include') to whatever apiEndpoint
// the config names, validate against an EXACT allowlist of known
// API hosts. A whole-domain suffix match (``*.attmosdiscovery.com``)
// would let a compromised or misconfigured config redirect
// authenticated traffic to ANY subdomain — including a takeover-
// prone one. The empty string + current origin always pass —
// that's the local-dev / Vite-proxy path.
//
// To add a new API host (e.g. a staging deploy) extend this set —
// one exact hostname per entry, never a wildcard or a suffix.
export const _ALLOWED_API_HOSTS: ReadonlySet<string> = new Set([
  // Production API (App Runner behind the custom domain).
  "flowstack-api.attmosdiscovery.com",
  // Development environment's API host — the App Runner custom domain
  // the dev SPA's portal-config.json names as its apiEndpoint
  // (RESOURCES.md "Development deployment — AWS"). Without it the dev
  // config is rejected here and apiFetch falls back to origin-relative,
  // which POSTs the Cognito token exchange into the SPA bucket and
  // breaks sign-in.
  "flowstackdev-api.attmosdiscovery.com",
]);

export function _isAllowedApiEndpoint(apiEndpoint: string): boolean {
  if (!apiEndpoint) return true;
  if (apiEndpoint === window.location.origin) return true;
  try {
    const url = new URL(apiEndpoint);
    // Must be HTTPS for a cross-origin endpoint (cookies marked
    // Secure won't ride along over http anyway).
    if (url.protocol !== "https:") return false;
    // Per Rupert PR-308 r2 #1: explicitly reject non-standard ports.
    // url.port is "" when the URL omits the port (i.e. defaults to
    // 443 for HTTPS); a value of "443" would also work but the
    // empty string is the canonical form. Anything else is rejected
    // so a host:8080 in the config can't slip past.
    if (url.port !== "" && url.port !== "443") return false;
    // Exact hostname match against the allowlist. Anchored — no
    // suffix matching — so ``evilflowstack-api.attmosdiscovery.com``
    // doesn't pass (Rupert PR-308 r2 #3 confirms this is the
    // correct framing for the credential-boundary check).
    return _ALLOWED_API_HOSTS.has(url.hostname);
  } catch {
    return false;
  }
}

/** Test-only: drop the portal-config cache so a fixture can stub it
 *  via vi.mock or vi.spyOn(global, "fetch"). */
export function _resetPortalConfigCacheForTesting(): void {
  _portalConfigCache = null;
  _portalConfigExpiresAt = 0;
}

/**
 * True when ``path`` is a safe, origin-relative API path — the only shape
 * `_resolveApiUrl` will resolve for a credentialed request.
 *
 * It must begin with a single ``/`` (so it stays on the current origin, or is
 * joined onto the configured API base) and carry no scheme-relative ``//host``
 * prefix nor a backslash the URL parser would fold into one (``/\host`` parses
 * as ``//host`` under a special scheme). This rejects absolute URLs
 * (`https://…`, `javascript:…`) and scheme-relative values, which — resolved
 * with `credentials: "include"` — would send the session cookie to a host the
 * server, not the client, names (flowstack#624).
 */
export function _isOriginRelativePath(path: string): boolean {
  return path.startsWith("/") && !path.startsWith("//") && !path.includes("\\");
}

/** Resolve ``path`` against the configured API endpoint.
 *
 *  When ``apiEndpoint`` is empty or matches the current origin (e.g.
 *  same-origin local dev with the Vite proxy), the path stays
 *  origin-relative. Otherwise it's joined onto the API base URL —
 *  preserving query strings. ``path`` must be origin-relative (enforced
 *  below); a non-relative value would let a credentialed fetch escape the
 *  API origin.
 */
function _resolveApiUrl(path: string, apiEndpoint: string): string {
  // Every apiFetch call site passes a compile-time literal ("/api/runs"), but
  // apiDownload's path originates from a server response and crosses a trust
  // boundary. Enforce origin-relativity HERE so no caller — present or future
  // — can resolve an absolute or scheme-relative URL that a credentialed fetch
  // would then send cross-origin. The property "the resolved path stays on our
  // origin/endpoint" is thus an enforced invariant of the resolver, not an
  // emergent consequence of how one caller builds its input (flowstack#624).
  // apiDownload additionally pins the exact payload shape before this point.
  if (!_isOriginRelativePath(path)) {
    throw new Error(
      "_resolveApiUrl: refusing a non-origin-relative path; credentialed " +
        "requests must stay same-origin / API-relative"
    );
  }
  if (!apiEndpoint) return path;
  // If the API endpoint is the current origin, no need to switch
  // hostnames; a relative fetch keeps cookies cleanly same-origin.
  if (apiEndpoint === window.location.origin) return path;
  // path is guaranteed to start with a single "/" (e.g. "/api/runs") by the
  // origin-relative guard above; we trim a trailing slash off apiEndpoint to
  // avoid "//api".
  const base = apiEndpoint.endsWith("/") ? apiEndpoint.slice(0, -1) : apiEndpoint;
  return `${base}${path}`;
}

export interface ApiErrorBody {
  error_code: string;
  message: string;
  details?: Record<string, unknown>;
}

export class ApiError extends Error {
  readonly status: number;
  readonly errorCode: string;
  readonly details?: Record<string, unknown>;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = "ApiError";
    this.status = status;
    this.errorCode = body.error_code;
    this.details = body.details;
  }
}

/**
 * Coerce a parsed error response into the {@link ApiErrorBody} shape.
 *
 * The backend emits the envelope two ways:
 *  - Top-level `{error_code, message, details?}` — the FastAPI
 *    exception handlers in `src/flowstack/server/errors.py`.
 *  - Nested `{detail: {error_code, message, details?}}` — any route
 *    that raises `HTTPException(detail={...})` (FastAPI wraps `detail`
 *    under a `detail` key). Most routes use this form
 *    (e.g. `POST /api/molecules`).
 *
 * `apiFetch` originally read only the top-level shape, so a nested
 * `HTTPException` produced an `ApiError` with an undefined `.message`
 * (the picker's `MoleculeUploadControl` surfaces `err.message`, so the
 * server's error text vanished). Unwrap `detail` when it carries an
 * `error_code` so both shapes yield the same `ApiErrorBody` (Rupert /
 * Copilot PR-513).
 */
export function _normalizeErrorBody(parsed: unknown): ApiErrorBody {
  if (typeof parsed !== "object" || parsed === null) {
    return { error_code: "unknown_error", message: "request failed" };
  }
  const top = parsed as Record<string, unknown>;
  const detail = top.detail;
  // FastAPI's `HTTPException(detail={error_code, message})` shape.
  if (
    typeof detail === "object" &&
    detail !== null &&
    typeof (detail as Record<string, unknown>).error_code === "string"
  ) {
    const d = detail as Record<string, unknown>;
    return {
      error_code: d.error_code as string,
      message: typeof d.message === "string" ? d.message : "request failed",
      details: d.details as Record<string, unknown> | undefined,
    };
  }
  // Already the top-level `{error_code, message}` shape.
  if (typeof top.error_code === "string") {
    return {
      error_code: top.error_code,
      message: typeof top.message === "string" ? top.message : "request failed",
      details: top.details as Record<string, unknown> | undefined,
    };
  }
  // `HTTPException(detail="a string")` or FastAPI's default 500 body
  // (`{"detail": "Internal Server Error"}`): keep the text as the message.
  if (typeof detail === "string") {
    return { error_code: "unknown_error", message: detail };
  }
  return { error_code: "unknown_error", message: "request failed" };
}

// In-memory ETag cache. Keyed by request URL (path + query) so two
// different filter combinations don't share an ETag entry. Capped
// at MAX_ETAG_ENTRIES with insertion-order eviction (oldest dropped
// when full) so a long-running tab visiting many run-detail pages
// can't leak the cache unbounded. Per Rupert's PR-253 review.
const MAX_ETAG_ENTRIES = 256;
const _etagCache = new Map<string, { etag: string; body: unknown }>();

function _setCached(path: string, etag: string, body: unknown): void {
  // Re-insert refreshes insertion order; if at capacity, drop the
  // oldest entry (Map iteration order is insertion order).
  if (_etagCache.has(path)) {
    _etagCache.delete(path);
  } else if (_etagCache.size >= MAX_ETAG_ENTRIES) {
    const oldest = _etagCache.keys().next().value;
    if (oldest !== undefined) {
      _etagCache.delete(oldest);
    }
  }
  _etagCache.set(path, { etag, body });
}

/** Test-only: drop the ETag cache so a fixture can start fresh. */
export function _resetETagCacheForTesting(): void {
  _etagCache.clear();
}

/**
 * Resolve an API path to its absolute URL, honoring the runtime
 * portal-config endpoint + allowlist. Shared by {@link apiFetch},
 * {@link apiDownload}, and raw-fetch callers (e.g. SSE streaming)
 * that need the same host resolution without JSON parsing.
 */
export async function resolveApiUrl(path: string): Promise<string> {
  const portalConfig = await _loadPortalConfig();
  const effectiveApiEndpoint = _isAllowedApiEndpoint(portalConfig.apiEndpoint)
    ? portalConfig.apiEndpoint
    : (() => {
        console.warn(
          `portal-config.json apiEndpoint ${JSON.stringify(portalConfig.apiEndpoint)} is ` +
            `not in the allowlist; falling back to origin-relative.`
        );
        return "";
      })();
  return _resolveApiUrl(path, effectiveApiEndpoint);
}

/**
 * How long to keep a download's blob URL alive after the click.
 *
 * Far beyond any browser's synchronous-start window, but short enough that a
 * multi-payload artifact (a ConformerEnsemble downloads its N geometries
 * sequentially) does not pin N whole payloads in tab memory (Rupert PR-622 r1
 * — 60s was two orders of magnitude more than needed). If the tab navigates
 * before the timer fires the URL is never revoked explicitly, which is benign:
 * unload reclaims it.
 */
const _OBJECT_URL_REVOKE_DELAY_MS = 5_000;

/**
 * The exact shape the server emits for a payload download route
 * (`src/flowstack/server/routes/artifacts.py::_payload_download_path`):
 * a single artifact-hash segment and a single payload-name segment, both
 * percent-encoded, so neither can contain a ``/``. `apiDownload`'s ``path`` is
 * server-response data (a persisted, catalog-rendered `download_url`) — unlike
 * every other apiFetch caller, which passes a compile-time literal — so it is
 * pinned to this shape at the boundary before any credentialed fetch. The
 * `[^/]+` segments admit the percent-encoded hash (`sha256%3A…`) and name.
 */
const _PAYLOAD_PATH_RE = /^\/api\/artifacts\/[^/]+\/payloads\/[^/]+$/;

/**
 * True when ``path`` is the API's own single-payload download route — the only
 * path `apiDownload` will fetch with credentials attached (flowstack#624).
 *
 * A conforming path is origin-relative by construction (exactly one leading
 * ``/``, no ``//host``, no authority component), so a credentialed download
 * can only ever target the configured API origin (the page origin when
 * ``apiEndpoint`` is unset) — never a host derived from response data. This
 * governs the *initial request URL* only: `fetch` follows redirects by
 * default, so the API could still redirect the request onward. Origin-scoped
 * cookies would not ride along to a cross-origin redirect target (no
 * credential-leak reopened), but the request/response could still leave the
 * API origin from that point on.
 */
export function _isSafePayloadPath(path: string): boolean {
  return _PAYLOAD_PATH_RE.test(path);
}

/**
 * Read one artifact payload as text, for in-page rendering.
 *
 * The sibling of {@link apiDownload}: same authenticated, tenant-scoped route
 * and the same `_isSafePayloadPath` guard on the server-supplied path, but the
 * bytes stay in the tab instead of becoming a file save. The molecule viewer
 * needs the structure text itself; saving it to disk and asking the user to
 * re-open it is not a viewer.
 *
 * `maxBytes` is a caller-supplied ceiling, checked against `Content-Length`
 * BEFORE the body is read, and again after — the header is advisory (absent
 * under a chunked or compressed response, and not something a client should
 * trust in any case), so the post-read check is what actually holds. The route
 * already refuses anything past `FLOWSTACK_MAX_PAYLOAD_DOWNLOAD_BYTES`
 * (`routes/artifacts.py`), but that cap is sized for *downloads* — what a tab
 * can save is not what a WebGL viewer should try to parse, so the render path
 * carries its own, much smaller number.
 *
 * Throws {@link ApiError} with `error_code: "payload_too_large"` when over the
 * ceiling, so a caller distinguishes "too big to draw" from "failed to fetch"
 * without string-matching a message.
 */
export async function apiFetchPayloadText(path: string, maxBytes: number): Promise<string> {
  if (!_isSafePayloadPath(path)) {
    throw new Error(
      "apiFetchPayloadText: refusing to fetch from an unexpected path; expected an " +
        "/api/artifacts/<hash>/payloads/<name> route on this API"
    );
  }
  const url = await resolveApiUrl(path);
  const response = await fetch(url, {
    credentials: "include",
    headers: { Accept: "application/octet-stream" },
  });

  if (!response.ok) {
    let body: ApiErrorBody;
    try {
      body = _normalizeErrorBody((await response.json()) as unknown);
    } catch {
      body = {
        error_code: "payload_fetch_failed",
        message: `Fetch of ${path} failed with status ${response.status}`,
      };
    }
    throw new ApiError(response.status, body);
  }

  const declared = Number(response.headers.get("Content-Length"));
  if (Number.isFinite(declared) && declared > maxBytes) {
    throw new ApiError(413, {
      error_code: "payload_too_large",
      message: `payload is ${declared} bytes; this view renders at most ${maxBytes} bytes`,
    });
  }

  const text = await response.text();
  // `text.length` counts UTF-16 code units, not bytes. Structure files are
  // ASCII in practice, but measuring the actual encoded size keeps the check
  // honest for a file carrying non-ASCII in a title or comment line.
  const size = new TextEncoder().encode(text).length;
  if (size > maxBytes) {
    throw new ApiError(413, {
      error_code: "payload_too_large",
      message: `payload is ${size} bytes; this view renders at most ${maxBytes} bytes`,
    });
  }
  return text;
}

/**
 * Fetch a binary payload and hand it to the browser as a file save.
 *
 * flowstack#619: artifact payloads are served from an authenticated,
 * tenant-scoped route (never a presigned S3 URL), so a plain `<a download>`
 * would not carry the session cookie cross-origin and would render the 401 in
 * a new tab. Fetching with `credentials: "include"` and saving the resulting
 * Blob keeps the download inside the same auth path every other call uses.
 *
 * The object URL is revoked in a `finally` so a failed save can't leak it.
 */
export async function apiDownload(path: string, filename: string): Promise<void> {
  // ``path`` is server-response data (`download_url`) crossing a trust boundary
  // into a `credentials: "include"` fetch. Pin it to the exact API payload
  // route shape BEFORE any fetch, so a poisoned or mis-generated value can
  // never become a credentialed cross-origin request (an absolute URL or a
  // `//host` scheme-relative reference) or otherwise escape the API origin — it
  // throws instead of being fetched (flowstack#624). This is the enforced
  // invariant; today's only caller happens to build a conforming path, but that
  // is no longer what the safety depends on.
  if (!_isSafePayloadPath(path)) {
    throw new Error(
      "apiDownload: refusing to download from an unexpected path; expected an " +
        "/api/artifacts/<hash>/payloads/<name> route on this API"
    );
  }
  const url = await resolveApiUrl(path);
  const response = await fetch(url, {
    credentials: "include",
    headers: { Accept: "application/octet-stream" },
  });

  if (!response.ok) {
    let body: ApiErrorBody;
    try {
      body = _normalizeErrorBody((await response.json()) as unknown);
    } catch {
      body = {
        error_code: "download_failed",
        message: `Download of ${path} failed with status ${response.status}`,
      };
    }
    throw new ApiError(response.status, body);
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } catch (error) {
    // The click never happened, so nothing can be reading the URL — reclaim it
    // immediately.
    URL.revokeObjectURL(objectUrl);
    throw error;
  }
  // Revoked on a later task, NOT synchronously (Rupert PR-622 r0). Chrome
  // starts the fetch for a programmatic blob download synchronously and
  // survives an immediate revoke, but Firefox and some Safari versions abort
  // it — silently: no error, no file. Deferring costs one queued task and
  // removes a browser-dependent failure the unit tests cannot see, because
  // they mock both click() and revokeObjectURL().
  setTimeout(() => URL.revokeObjectURL(objectUrl), _OBJECT_URL_REVOKE_DELAY_MS);
}

export async function apiFetch<T>(
  path: string,
  init?: RequestInit & { json?: unknown }
): Promise<T> {
  const { json, headers, ...rest } = init ?? {};
  const method = (rest.method ?? "GET").toUpperCase();
  const isGet = method === "GET";

  // Resolve the API URL from the runtime portal-config.json. Local
  // dev with the Vite proxy keeps things origin-relative; production
  // S3+CloudFront → cross-origin to flowstack-api.attmosdiscovery.com.
  const portalConfig = await _loadPortalConfig();
  // Per Rupert PR-308 r1 #1: validate the endpoint before trusting
  // it with credentials. A rewritten S3 object can't redirect
  // authenticated traffic to an arbitrary host.
  const effectiveApiEndpoint = _isAllowedApiEndpoint(portalConfig.apiEndpoint)
    ? portalConfig.apiEndpoint
    : (() => {
        console.warn(
          `portal-config.json apiEndpoint ${JSON.stringify(portalConfig.apiEndpoint)} is ` +
            `not in the allowlist; falling back to origin-relative.`
        );
        return "";
      })();
  const url = _resolveApiUrl(path, effectiveApiEndpoint);
  // Per Rupert PR-308 r1 #10: ETag cache key must include the
  // resolved API origin so a portal-config flip can't re-use a
  // stale ETag from a previous origin for the wrong backend.
  const cacheKey = `${effectiveApiEndpoint}::${path}`;
  const cached = isGet ? _etagCache.get(cacheKey) : undefined;
  // ``include`` ships the session cookie cross-origin; for same-origin
  // dev it behaves identically to ``same-origin``. The backend's
  // SessionMiddleware + CORS allow-credentials must match (covered by
  // the production deploy config, not this hunk).
  const credentials: RequestCredentials = "include";

  const response = await fetch(url, {
    ...rest,
    credentials,
    headers: {
      Accept: "application/json",
      ...(json !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(cached !== undefined ? { "If-None-Match": cached.etag } : {}),
      ...headers,
    },
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  });

  if (response.status === 304 && cached !== undefined) {
    // Nothing changed since last fetch — return the cached body.
    return cached.body as T;
  }

  if (!response.ok) {
    let body: ApiErrorBody;
    try {
      body = _normalizeErrorBody((await response.json()) as unknown);
    } catch {
      body = {
        error_code: "unknown_error",
        message: `Request to ${path} failed with status ${response.status}`,
      };
    }
    throw new ApiError(response.status, body);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const body = (await response.json()) as T;
  const etag = response.headers.get("etag");
  if (isGet && etag) {
    // Cache key is (apiEndpoint, path) so an apiEndpoint flip
    // doesn't reuse an ETag from a different origin (Rupert PR-308
    // r1 #10).
    _setCached(cacheKey, etag, body);
  }
  return body;
}
